// Rep counting state machines for push-up, pull-up, squat, lunge.
// Each counter consumes pose keypoints (MoveNet/MediaPipe-compatible) and emits {count, depth, formScore, lastRepValid}.
// Keypoints use the COCO-17 schema indices that MoveNet returns.

export const ML_EXERCISE_MAP = {
    "push-up": "pushup",
    "pull-up": "pullup",
    "squat": "squat",
    "weighted-squat": "squat",
    "lunge": "lunge",
};

const IDX = {
    nose: 0,
    left_shoulder: 5, right_shoulder: 6,
    left_elbow: 7, right_elbow: 8,
    left_wrist: 9, right_wrist: 10,
    left_hip: 11, right_hip: 12,
    left_knee: 13, right_knee: 14,
    left_ankle: 15, right_ankle: 16,
};

function angle(a, b, c) {
    const ab = { x: a.x - b.x, y: a.y - b.y };
    const cb = { x: c.x - b.x, y: c.y - b.y };
    const dot = ab.x * cb.x + ab.y * cb.y;
    const magAB = Math.hypot(ab.x, ab.y);
    const magCB = Math.hypot(cb.x, cb.y);
    if (magAB === 0 || magCB === 0) return null;
    const cos = Math.max(-1, Math.min(1, dot / (magAB * magCB)));
    return (Math.acos(cos) * 180) / Math.PI;
}

function avgConf(pts) {
    const vals = pts.map((p) => p?.score || 0);
    return vals.reduce((s, v) => s + v, 0) / Math.max(1, vals.length);
}

function midPoint(a, b) {
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

class BaseCounter {
    constructor() {
        this.state = "up";
        this.count = 0;
        this.lastRepValid = true;
        this.depth = 0;        // 0..1 (1 = full depth)
        this.formScore = 1;     // ema 0..1
        this.lowestDepthInRep = 0;
        this.repsValid = 0;
        this.repsTotal = 0;
    }
    _record(valid) {
        this.repsTotal += 1;
        if (valid) {
            this.count += 1;
            this.repsValid += 1;
            this.lastRepValid = true;
        } else {
            this.lastRepValid = false;
        }
        this.formScore = this.repsTotal > 0 ? this.repsValid / this.repsTotal : 1;
    }
}

// --- Push-Up: track avg elbow angle. Down when angle < downAngle, up when > upAngle.
//     Depth = chest near hands (elbow angle <= 90° valid)
class PushUpCounter extends BaseCounter {
    constructor() {
        super();
        this.upAngle = 155;
        this.downAngle = 100;
        this.depthValidAngle = 95; // need elbows ≤ 95° for valid depth
    }
    update(kps) {
        const ls = kps[IDX.left_shoulder], le = kps[IDX.left_elbow], lw = kps[IDX.left_wrist];
        const rs = kps[IDX.right_shoulder], re = kps[IDX.right_elbow], rw = kps[IDX.right_wrist];
        if (avgConf([ls, le, lw, rs, re, rw]) < 0.3) return this;
        const aL = angle(ls, le, lw);
        const aR = angle(rs, re, rw);
        if (aL == null && aR == null) return this;
        const a = (aL != null && aR != null) ? (aL + aR) / 2 : (aL ?? aR);
        // Map angle to depth [0..1], 180=up, 80=deep
        this.depth = Math.max(0, Math.min(1, (180 - a) / 100));
        if (this.state === "up" && a < this.downAngle) {
            this.state = "down";
            this.lowestDepthInRep = a;
        } else if (this.state === "down") {
            if (a < this.lowestDepthInRep) this.lowestDepthInRep = a;
            if (a > this.upAngle) {
                this._record(this.lowestDepthInRep <= this.depthValidAngle);
                this.state = "up";
                this.lowestDepthInRep = 0;
            }
        }
        return this;
    }
}

// --- Pull-Up: track elbow angle inverted (arms straight = down, chin to bar = up).
//     Use vertical position of nose relative to wrists for additional gating.
class PullUpCounter extends BaseCounter {
    constructor() {
        super();
        this.downAngle = 155;
        this.upAngle = 80;
        this.depthValidAngle = 90;
    }
    update(kps) {
        const ls = kps[IDX.left_shoulder], le = kps[IDX.left_elbow], lw = kps[IDX.left_wrist];
        const rs = kps[IDX.right_shoulder], re = kps[IDX.right_elbow], rw = kps[IDX.right_wrist];
        const nose = kps[IDX.nose];
        if (avgConf([ls, le, lw, rs, re, rw, nose]) < 0.3) return this;
        const aL = angle(ls, le, lw);
        const aR = angle(rs, re, rw);
        if (aL == null && aR == null) return this;
        const a = (aL != null && aR != null) ? (aL + aR) / 2 : (aL ?? aR);
        this.depth = Math.max(0, Math.min(1, (180 - a) / 100));
        const wristY = Math.min(lw.y, rw.y);
        const chinAboveBar = nose && wristY && nose.y < wristY + 10;
        if (this.state === "down" && a < this.upAngle) {
            this.state = "up";
            this.lowestDepthInRep = a;
        } else if (this.state === "up") {
            if (a < this.lowestDepthInRep) this.lowestDepthInRep = a;
            if (a > this.downAngle) {
                this._record(this.lowestDepthInRep <= this.depthValidAngle && (chinAboveBar !== false));
                this.state = "down";
                this.lowestDepthInRep = 0;
            }
        }
        return this;
    }
}

// --- Squat: knee angle. Down when knees ≤ 100°, up when ≥ 160°. Depth valid if hip below knee (parallel).
class SquatCounter extends BaseCounter {
    constructor() {
        super();
        this.upAngle = 160;
        this.downAngle = 110;
        this.depthValidAngle = 95;
    }
    update(kps) {
        const lh = kps[IDX.left_hip], lk = kps[IDX.left_knee], la = kps[IDX.left_ankle];
        const rh = kps[IDX.right_hip], rk = kps[IDX.right_knee], ra = kps[IDX.right_ankle];
        if (avgConf([lh, lk, la, rh, rk, ra]) < 0.3) return this;
        const aL = angle(lh, lk, la);
        const aR = angle(rh, rk, ra);
        if (aL == null && aR == null) return this;
        const a = (aL != null && aR != null) ? (aL + aR) / 2 : (aL ?? aR);
        this.depth = Math.max(0, Math.min(1, (180 - a) / 100));
        // Parallel check: hip y >= knee y (image coords: larger y = lower on screen)
        const hipY = (lh.y + rh.y) / 2;
        const kneeY = (lk.y + rk.y) / 2;
        const belowParallel = hipY >= kneeY - 5;
        if (this.state === "up" && a < this.downAngle) {
            this.state = "down";
            this.lowestDepthInRep = a;
            this._belowParallelInRep = belowParallel;
        } else if (this.state === "down") {
            if (a < this.lowestDepthInRep) this.lowestDepthInRep = a;
            if (belowParallel) this._belowParallelInRep = true;
            if (a > this.upAngle) {
                this._record(this.lowestDepthInRep <= this.depthValidAngle && !!this._belowParallelInRep);
                this.state = "up";
                this.lowestDepthInRep = 0;
                this._belowParallelInRep = false;
            }
        }
        return this;
    }
}

// --- Lunge: front knee angle. Same threshold as squat but unilateral. Use the deeper knee.
class LungeCounter extends BaseCounter {
    constructor() {
        super();
        this.upAngle = 160;
        this.downAngle = 110;
        this.depthValidAngle = 100;
    }
    update(kps) {
        const lh = kps[IDX.left_hip], lk = kps[IDX.left_knee], la = kps[IDX.left_ankle];
        const rh = kps[IDX.right_hip], rk = kps[IDX.right_knee], ra = kps[IDX.right_ankle];
        if (avgConf([lh, lk, la, rh, rk, ra]) < 0.3) return this;
        const aL = angle(lh, lk, la);
        const aR = angle(rh, rk, ra);
        if (aL == null && aR == null) return this;
        const a = Math.min(aL ?? 180, aR ?? 180); // the front (deeper-bent) knee
        this.depth = Math.max(0, Math.min(1, (180 - a) / 100));
        if (this.state === "up" && a < this.downAngle) {
            this.state = "down";
            this.lowestDepthInRep = a;
        } else if (this.state === "down") {
            if (a < this.lowestDepthInRep) this.lowestDepthInRep = a;
            if (a > this.upAngle) {
                this._record(this.lowestDepthInRep <= this.depthValidAngle);
                this.state = "up";
                this.lowestDepthInRep = 0;
            }
        }
        return this;
    }
}

export function makeCounter(mlKey) {
    switch (mlKey) {
        case "pushup": return new PushUpCounter();
        case "pullup": return new PullUpCounter();
        case "squat": return new SquatCounter();
        case "lunge": return new LungeCounter();
        default: return null;
    }
}

export function getMlKey(exercise_type) {
    if (!exercise_type) return null;
    return ML_EXERCISE_MAP[exercise_type] || null;
}
